// Canvas-based post-processing effects applied during export
import type {PostProcessParams} from '../types/postProcessTypes'

// ── Helpers ───────────────────────────────────────────────────────

/** Clone canvas pixels into a new canvas of the same size */
const cloneCanvas = (src: HTMLCanvasElement): HTMLCanvasElement => {
  const c = document.createElement('canvas')
  c.width = src.width
  c.height = src.height
  c.getContext('2d')!.drawImage(src, 0, 0)
  return c
}

/** Simple box-blur applied to ImageData (in-place) */
const boxBlur = (data: ImageData, radius: number) => {
  const w = data.width
  const h = data.height
  const px = data.data
  const out = new Uint8ClampedArray(px.length)

  const size = radius * 2 + 1

  // Horizontal pass
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0
      let g = 0
      let b = 0
      let a = 0
      for (let dx = -radius; dx <= radius; dx++) {
        const sx = Math.min(w - 1, Math.max(0, x + dx))
        const i = (y * w + sx) * 4
        r += px[i]
        g += px[i + 1]
        b += px[i + 2]
        a += px[i + 3]
      }
      const i = (y * w + x) * 4
      out[i] = r / size
      out[i + 1] = g / size
      out[i + 2] = b / size
      out[i + 3] = a / size
    }
  }

  // Vertical pass
  px.set(out)
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let r = 0
      let g = 0
      let b = 0
      let a = 0
      for (let dy = -radius; dy <= radius; dy++) {
        const sy = Math.min(h - 1, Math.max(0, y + dy))
        const i = (sy * w + x) * 4
        r += px[i]
        g += px[i + 1]
        b += px[i + 2]
        a += px[i + 3]
      }
      const i = (y * w + x) * 4
      out[i] = r / size
      out[i + 1] = g / size
      out[i + 2] = b / size
      out[i + 3] = a / size
    }
  }
  data.data.set(out)
}

// ── Effects ───────────────────────────────────────────────────────

/** Increase global contrast via S-curve around midpoint */
const applyContrast = (canvas: HTMLCanvasElement, amount: number) => {
  if (amount <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height
  const imgData = ctx.getImageData(0, 0, w, h)
  const px = imgData.data

  // S-curve: map 0–1 through a power curve that pivots at 0.5
  const factor = 1 + amount * 5 // e.g. 0.02 → 1.1 (gentle)
  for (let i = 0; i < px.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const val = px[i + c] / 255
      // S-curve: shift to center, scale, shift back
      const adjusted = ((val - 0.5) * factor + 0.5) * 255
      px[i + c] = Math.min(255, Math.max(0, adjusted))
    }
  }
  ctx.putImageData(imgData, 0, 0)
}

/** Small exposure (EV) adjustment — multiplies RGB values */
const applyExposure = (canvas: HTMLCanvasElement, ev: number) => {
  if (ev <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height
  const imgData = ctx.getImageData(0, 0, w, h)
  const px = imgData.data

  const multiplier = Math.pow(2, ev)
  for (let i = 0; i < px.length; i += 4) {
    px[i] = Math.min(255, px[i] * multiplier)
    px[i + 1] = Math.min(255, px[i + 1] * multiplier)
    px[i + 2] = Math.min(255, px[i + 2] * multiplier)
  }
  ctx.putImageData(imgData, 0, 0)
}

/** Subtle saturation increase — adjusts distance from luminance */
const applySaturation = (canvas: HTMLCanvasElement, amount: number) => {
  if (amount <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height
  const imgData = ctx.getImageData(0, 0, w, h)
  const px = imgData.data

  const boost = 1 + amount
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]
    const g = px[i + 1]
    const b = px[i + 2]
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
    px[i] = Math.min(255, Math.max(0, luma + boost * (r - luma)))
    px[i + 1] = Math.min(255, Math.max(0, luma + boost * (g - luma)))
    px[i + 2] = Math.min(255, Math.max(0, luma + boost * (b - luma)))
  }
  ctx.putImageData(imgData, 0, 0)
}

/** Micro-contrast / clarity — enhanced local contrast via highpass blend */
const applyClarity = (canvas: HTMLCanvasElement, amount: number) => {
  if (amount <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height
  const original = ctx.getImageData(0, 0, w, h)
  const blurred = ctx.getImageData(0, 0, w, h)

  // Larger blur radius for clarity (local contrast) vs sharpening
  boxBlur(blurred, 6)
  boxBlur(blurred, 6)

  const op = original.data
  const bp = blurred.data
  for (let i = 0; i < op.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      // Highpass = original - blurred, blend back at amount strength
      const diff = op[i + c] - bp[i + c]
      op[i + c] = Math.min(255, Math.max(0, op[i + c] + diff * amount))
    }
  }
  ctx.putImageData(original, 0, 0)
}

/** Unsharp mask: sharpen by subtracting a blurred version */
const applyUnsharpMask = (canvas: HTMLCanvasElement, amount: number) => {
  if (amount <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height
  const original = ctx.getImageData(0, 0, w, h)
  const blurred = ctx.getImageData(0, 0, w, h)
  boxBlur(blurred, 2)

  const op = original.data
  const bp = blurred.data
  for (let i = 0; i < op.length; i += 4) {
    op[i] = Math.min(255, Math.max(0, op[i] + (op[i] - bp[i]) * amount))
    op[i + 1] = Math.min(255, Math.max(0, op[i + 1] + (op[i + 1] - bp[i + 1]) * amount))
    op[i + 2] = Math.min(255, Math.max(0, op[i + 2] + (op[i + 2] - bp[i + 2]) * amount))
  }
  ctx.putImageData(original, 0, 0)
}

/** Radial vignette — darken corners with configurable radius */
const applyVignette = (canvas: HTMLCanvasElement, strength: number, radius: number) => {
  if (strength <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height
  const cx = w / 2
  const cy = h / 2
  const maxR = Math.sqrt(cx * cx + cy * cy)
  const gradient = ctx.createRadialGradient(cx, cy, maxR * radius, cx, cy, maxR)
  gradient.addColorStop(0, 'rgba(0,0,0,0)')
  gradient.addColorStop(1, `rgba(0,0,0,${strength})`)
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, w, h)
}

/** Ambient darkening — very gentle edge darkening from all four sides */
const applyAmbientDarkening = (canvas: HTMLCanvasElement, strength: number) => {
  if (strength <= 0) return
  const ctx = canvas.getContext('2d')!
  const w = canvas.width
  const h = canvas.height

  // Darken from each edge with a short linear gradient
  const edgeDepth = 0.15 // fraction of dimension
  const alpha = strength * 0.5 // keep it very subtle

  // Top edge
  const topGrad = ctx.createLinearGradient(0, 0, 0, h * edgeDepth)
  topGrad.addColorStop(0, `rgba(0,0,0,${alpha})`)
  topGrad.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = topGrad
  ctx.fillRect(0, 0, w, h * edgeDepth)

  // Bottom edge
  const botGrad = ctx.createLinearGradient(0, h, 0, h * (1 - edgeDepth))
  botGrad.addColorStop(0, `rgba(0,0,0,${alpha})`)
  botGrad.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = botGrad
  ctx.fillRect(0, h * (1 - edgeDepth), w, h * edgeDepth)

  // Left edge
  const leftGrad = ctx.createLinearGradient(0, 0, w * edgeDepth, 0)
  leftGrad.addColorStop(0, `rgba(0,0,0,${alpha})`)
  leftGrad.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = leftGrad
  ctx.fillRect(0, 0, w * edgeDepth, h)

  // Right edge
  const rightGrad = ctx.createLinearGradient(w, 0, w * (1 - edgeDepth), 0)
  rightGrad.addColorStop(0, `rgba(0,0,0,${alpha})`)
  rightGrad.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = rightGrad
  ctx.fillRect(w * (1 - edgeDepth), 0, w * edgeDepth, h)
}

// ── Orchestrator ──────────────────────────────────────────────────

/** Apply all post-processing effects with given parameters */
export const applyPostProcessing = (canvas: HTMLCanvasElement, params: PostProcessParams): HTMLCanvasElement => {
  const result = cloneCanvas(canvas)

  // 1. Contrast
  applyContrast(result, params.contrast)
  // 2. Exposure
  applyExposure(result, params.exposure)
  // 3. Saturation
  applySaturation(result, params.saturation)
  // 4. Clarity (micro-contrast)
  applyClarity(result, params.clarity)
  // 5. Sharpening (unsharp mask)
  applyUnsharpMask(result, params.sharpening)
  // 6. Vignette
  applyVignette(result, params.vignetteStrength, params.vignetteRadius)
  // 7. Ambient edge darkening
  applyAmbientDarkening(result, params.vignetteStrength)

  return result
}
